import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Create a stable axios instance using useMemo
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: `${API_URL}/api`,
    });
    
    // Add request interceptor to always use the latest token
    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });
    
    return instance;
  }, []); // Empty dependency array - instance is created only once

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token, api]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const { token: newToken, user: newUser } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(newUser);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId) => {
    try {
      const response = await api.get(`/auth/session?session_id=${sessionId}`);
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      return { success: true, needsOnboarding: userData.tenant_id === 'default' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Google login failed' };
    }
  };

  const completeOnboarding = async (tenantId, role) => {
    try {
      const response = await api.put(`/auth/onboarding?tenant_id=${tenantId}${role ? `&role=${role}` : ''}`);
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Onboarding failed' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    
    // Para permissão de criar atos, usar a flag do backend (fonte da verdade)
    if (permission === 'create') {
      return user.can_create_act === true;
    }
    
    // Permissões por role (ATUALIZADO conforme regras de negócio)
    const rolePermissions = {
      ADMIN_ESTADUAL: ["edit", "distribute", "validate", "upload_evidence", "approve", "respond", "close", "view", "audit", "manage_users"],
      CASA_CIVIL: ["edit", "distribute", "validate", "approve", "respond", "close", "view", "audit"],
      CGE: ["create", "edit", "distribute", "validate", "upload_evidence", "approve", "respond", "view", "audit"],
      CONTROLE_INTERNO: ["create", "edit", "distribute", "validate", "upload_evidence", "approve", "respond", "view", "audit"],
      AUDITORIA: ["create", "edit", "distribute", "validate", "upload_evidence", "approve", "respond", "view", "audit"],
      SECRETARIA_EXECUTORA: ["edit", "upload_evidence", "respond", "view"],
      UNIDADE_INTERNA: ["upload_evidence", "view"],
      JURIDICO: ["edit", "validate", "respond", "view"],
      GOVERNADOR: ["view"],
      OBSERVADOR_EXTERNO: ["view"]
    };
    return rolePermissions[user.role]?.includes(permission) || false;
  };

  // Função específica para verificar se pode criar atos (usa flag do backend)
  const canCreateAct = () => {
    if (!user) return false;
    return user.can_create_act === true;
  };

  // Define a sessão a partir de um JWT recebido por SSO externo (Azure AD).
  const setAuthFromExternalToken = useCallback(async (externalToken) => {
    if (!externalToken) throw new Error('Token externo ausente');
    localStorage.setItem('token', externalToken);
    setToken(externalToken);
    // Carrega o /auth/me usando o token recém-salvo via interceptor
    const me = await api.get('/auth/me');
    setUser(me.data);
    return me.data;
  }, [api]);

  const value = {
    user,
    token,
    loading,
    login,
    register,
    loginWithGoogle,
    handleGoogleCallback,
    completeOnboarding,
    logout,
    hasPermission,
    canCreateAct,
    setAuthFromExternalToken,
    api,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
